"use client";

import { createContext, Suspense, use, useContext, type PropsWithChildren } from "react";
import type {
	AsyncSegment,
	SegmentControlContext as ISegmentControlContext,
	Segments,
} from "./types";
import { useSegmentControlMatcher } from "./control";

export const SegmentControlContext = createContext<ISegmentControlContext>({
	segments: {},
	hasSegmentValues: false,
});

export const SegmentControlProvider = ({
	children,
	segments,
}: PropsWithChildren<{ segments: Record<string, AsyncSegment> }>) => {
	const hasSegmentValues = Object.entries(segments).some(([key, segment]) => !!segment);

	return (
		<SegmentControlContext.Provider value={{ segments, hasSegmentValues }}>
			{children}
		</SegmentControlContext.Provider>
	);
};

export const SegmentControl = ({
	children,
	segments,
	fallback,
}: PropsWithChildren<{
	segments: Segments;
	fallback?: React.ReactNode;
}>) => {
	const { hasSegmentValues } = useContext(SegmentControlContext);

	if (!hasSegmentValues) return <>{children}</>;

	const segmentEntries = Object.entries(segments);

	const hasSegments = segmentEntries.some(
		([key, segments]) => !!segments && Array.isArray(segments) && segments.length > 0,
	);

	if (!hasSegments) return <>{children}</>;

	return (
		<Suspense fallback={fallback}>
			<SegmentsMatcher entries={segments}>{children}</SegmentsMatcher>
		</Suspense>
	);
};

const SegmentsMatcher = ({
	entries,
	children,
}: PropsWithChildren<{
	entries: Segments;
}>) => {
	const { segments: values } = use(SegmentControlContext);
	const { rule } = useSegmentControlMatcher(entries, values);

	if (rule === "DENY") return null;

	return <>{children}</>;
};
